/**
 * M타입 - 객관식 입력 컴포넌트
 * 5지선다 (①②③④⑤)
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';

/**
 * MultipleChoiceInput Props
 */
interface MultipleChoiceInputProps {
  /** 답안 제출 콜백 */
  onSubmit: (answer: string) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * M타입 객관식 입력 컴포넌트
 *
 * - 5개의 픽셀 버튼 (①②③④⑤)
 * - 단일/다중 선택 지원 (정답이 "1,2" 형식이면 다중)
 * - 선택된 버튼 하이라이트
 * - 제출 버튼
 */
export const MultipleChoiceInput: React.FC<MultipleChoiceInputProps> = ({
  onSubmit,
  disabled = false,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  /**
   * 선택지 버튼 클릭 핸들러 (토글)
   */
  const handleToggle = (choice: number) => {
    if (disabled) return;

    setSelected((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(choice)) {
        newSet.delete(choice);
      } else {
        newSet.add(choice);
      }
      return newSet;
    });
  };

  /**
   * 제출 핸들러
   */
  const handleSubmit = () => {
    if (selected.size === 0 || disabled) return;

    // 선택된 답을 정렬하여 쉼표로 연결
    const answer = Array.from(selected).sort().join(',');
    onSubmit(answer);

    // 제출 후 초기화
    setSelected(new Set());
  };

  const choices = [1, 2, 3, 4, 5];

  return (
    <div className="space-y-2">
      {/* 안내 메시지 */}
      <p className="text-center font-pixel text-xs text-gray-400">
        정답을 선택하세요 (다중 선택 가능)
      </p>

      {/* 선택지 버튼 */}
      <div className="grid grid-cols-5 gap-2">
        {choices.map((choice) => {
          const isSelected = selected.has(choice);
          return (
            <PixelButton
              key={choice}
              variant={isSelected ? 'primary' : 'secondary'}
              size="large"
              onClick={() => handleToggle(choice)}
              disabled={disabled}
              className="aspect-square text-2xl relative"
            >
              {/* 체크 표시 */}
              {isSelected && (
                <span className="absolute top-0 right-1 text-xs">✓</span>
              )}
              {['①', '②', '③', '④', '⑤'][choice - 1]}
            </PixelButton>
          );
        })}
      </div>

      {/* 선택된 답 표시 */}
      {selected.size > 0 && (
        <p className="text-center font-pixel text-sm text-[#9bbc0f]">
          선택: {Array.from(selected).sort().join(', ')}
        </p>
      )}

      {/* 제출 버튼 */}
      <PixelButton
        variant="primary"
        size="small"
        onClick={handleSubmit}
        disabled={selected.size === 0 || disabled}
        className="w-full"
      >
        {selected.size === 0 ? '선택해주세요' : '제출하기'}
      </PixelButton>
    </div>
  );
};
